/**
 * Brain Missions · React Query hooks — Milestone 37.4.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  applyBrainAnswer,
  approveBrain,
  BrainMissionType,
  fetchBrainCatalog,
  fetchBrainGate,
  fetchBrainState,
  skipBrainQuestion,
  submitBrainForReview,
} from "./api";

const kCatalog = (ws: string, type: BrainMissionType) =>
  ["brain-mission-catalog", ws, type] as const;
const kState = (ws: string, type: BrainMissionType) =>
  ["brain-mission-state", ws, type] as const;
const kGate = (ws: string) => ["brain-mission-gate", ws] as const;

export function useBrainCatalog(ws: string | undefined, type: BrainMissionType) {
  return useQuery({
    queryKey: ws ? kCatalog(ws, type) : ["brain-mission-catalog", "none", type],
    queryFn: () => fetchBrainCatalog(ws!, type),
    enabled: !!ws,
    staleTime: 24 * 60 * 60 * 1000, // Catalog is immutable at deploy time.
  });
}

export function useBrainState(ws: string | undefined, type: BrainMissionType) {
  return useQuery({
    queryKey: ws ? kState(ws, type) : ["brain-mission-state", "none", type],
    queryFn: () => fetchBrainState(ws!, type),
    enabled: !!ws,
  });
}

export function useBrainGate(ws: string | undefined) {
  return useQuery({
    queryKey: ws ? kGate(ws) : ["brain-mission-gate", "none"],
    queryFn: () => fetchBrainGate(ws!),
    enabled: !!ws,
  });
}

export function useAnswerBrainQuestion(ws: string, type: BrainMissionType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { question_id: string; value: unknown }) =>
      applyBrainAnswer(ws, type, input),
    onSuccess: (state) => {
      qc.setQueryData(kState(ws, type), state);
      qc.invalidateQueries({ queryKey: kGate(ws) });
    },
  });
}

export function useSkipBrainQuestion(ws: string, type: BrainMissionType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { question_id: string; skipped?: boolean }) =>
      skipBrainQuestion(ws, type, input),
    onSuccess: (state) => {
      qc.setQueryData(kState(ws, type), state);
    },
  });
}

export function useSubmitBrainForReview(ws: string, type: BrainMissionType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => submitBrainForReview(ws, type),
    onSuccess: (res) => {
      qc.setQueryData(kState(ws, type), res.state);
      qc.invalidateQueries({ queryKey: kGate(ws) });
    },
  });
}

export function useApproveBrain(ws: string, type: BrainMissionType) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => approveBrain(ws, type),
    onSuccess: (res) => {
      qc.setQueryData(kState(ws, type), res.state);
      qc.invalidateQueries({ queryKey: kGate(ws) });
      qc.invalidateQueries({ queryKey: ["intelligence-status"] });
    },
  });
}
